import axios from "axios";
import qs from "qs";
import config from "@/config";
import storage from "@/utils/storage";

const baseUrl = process.env.NODE_ENV === "development" ? config.baseUrl.dev : config.baseUrl.pro;
const refreshAheadSeconds = 60;
let authPromise = null;

const decodePayload = (token) => {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
    return JSON.parse(decodeURIComponent(escape(window.atob(padded))));
  } catch (e) {
    return null;
  }
};

// 仅用于前端展示 JWT 载荷，不代表验签成功；验签始终由 zswl 后端完成。
export const decodeAccessTokenPayload = (token) => decodePayload(token);

const authRequest = (url, data) => axios.post(`${baseUrl}${url}`, qs.stringify(data), {
  headers: { "Content-Type": "application/x-www-form-urlencoded" },
  timeout: 30 * 1000,
}).then((response) => {
  const result = response.data || {};
  if (result.code !== 1 || !result.data || !result.data.token) {
    const error = new Error(result.msg || "登录失败");
    error.response = response;
    throw error;
  }
  return result.data;
});

const saveTokens = (tokens) => {
  storage.set("token", tokens.token);
  storage.set("refreshToken", tokens.refreshToken);
  storage.set("tokenExpiresAt", Number(tokens.expiresAt || 0));
  return tokens.token;
};

export const clearAppTokens = () => {
  storage.remove("token");
  storage.remove("refreshToken");
  storage.remove("tokenExpiresAt");
};

export const isBackendAccessTokenUsable = (accountId, aheadSeconds = refreshAheadSeconds) => {
  const payload = decodePayload(storage.get("token"));
  if (!payload || payload.typ !== "access" || !payload.data) return false;
  if (accountId && String(payload.data.account_id) !== String(accountId)) return false;
  return Number(payload.exp || 0) > Math.floor(Date.now() / 1000) + aheadSeconds;
};

const exchangeSession = (customerInfo) => {
  const accountId = customerInfo && customerInfo.account_id;
  const sessionId = storage.get("uniqueId");
  if (!accountId || !sessionId) {
    return Promise.reject(new Error("未获取到掌上温岭登录会话，请重新登录"));
  }
  return authRequest("zswl_user/session_login", {
    accountId,
    sessionId,
  }).then(saveTokens);
};

const rotateRefreshToken = () => {
  const refreshToken = storage.get("refreshToken");
  if (!refreshToken) return Promise.reject(new Error("refreshToken不存在"));
  return authRequest("zswl_user/refresh_token", { refreshToken }).then(saveTokens);
};

const runOnce = (factory) => {
  if (authPromise) return authPromise;
  authPromise = Promise.resolve().then(factory).finally(() => {
    authPromise = null;
  });
  return authPromise;
};

export const ensureAppSession = (customerInfo) => runOnce(async () => {
  const accountId = customerInfo && customerInfo.account_id;
  const payload = decodePayload(storage.get("token"));
  const previousAccountId = payload && payload.data ? payload.data.account_id : storage.get("accountId");
  const accountChanged = Boolean(previousAccountId && String(previousAccountId) !== String(accountId));
  if (accountChanged) clearAppTokens();

  if (isBackendAccessTokenUsable(accountId)) {
    return { token: storage.get("token"), accountChanged };
  }

  if (!accountChanged && storage.get("refreshToken")) {
    try {
      const token = await rotateRefreshToken();
      return { token, accountChanged };
    } catch (e) {
      clearAppTokens();
    }
  }

  const token = await exchangeSession(customerInfo);
  return { token, accountChanged };
});

export const createDebugAppSession = (mobile) => runOnce(async () => {
  mobile = String(mobile || "").trim();
  if (!/^1[3-9]\d{9}$/.test(mobile)) throw new Error("请输入正确的手机号码");

  const tokens = await authRequest("zswl_user/debug_login", { mobile });
  const appUser = tokens.appUser;
  if (!appUser || !appUser.account_id || String(appUser.mobile) !== mobile) {
    throw new Error("模拟登录未返回有效用户资料");
  }
  const accountId = appUser.account_id;
  const previousAccountId = storage.get("accountId");
  const accountChanged = Boolean(previousAccountId && String(previousAccountId) !== String(accountId));

  clearAppTokens();
  const token = saveTokens(tokens);
  storage.remove("uniqueId");
  storage.set("appUser", appUser);
  storage.set("accountId", accountId);
  if (accountChanged) {
    storage.set("hasUser", false);
    storage.remove("zswlUser");
  }

  return { token, accountChanged, appUser };
});

export const ensureFreshAccessToken = (force = false) => runOnce(async () => {
  if (!force && isBackendAccessTokenUsable()) return storage.get("token");

  if (storage.get("refreshToken")) {
    try {
      return await rotateRefreshToken();
    } catch (e) {
      clearAppTokens();
    }
  }

  const appUser = storage.get("appUser");
  if (appUser && appUser.account_id && storage.get("uniqueId")) {
    return exchangeSession(appUser);
  }
  return null;
});

export const forceRefreshAccessToken = () => ensureFreshAccessToken(true);

